import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowLeft, Search, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function StudentPokedex() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ entries: [], captured_count: 0, total: 150 });
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all'); // all | captured | locked
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/student/pokedex`, { credentials: 'include' });
        if (!r.ok) throw new Error('Não foi possível carregar a Pokédex');
        const d = await r.json();
        setData(d);
      } catch (e) {
        toast.error(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    return (data.entries || []).filter((e) => {
      if (filter === 'captured' && !e.captured) return false;
      if (filter === 'locked' && e.captured) return false;
      if (!q.trim()) return true;
      const needle = q.trim().toLowerCase();
      return (
        e.name.toLowerCase().includes(needle) ||
        String(e.pokemon_id).padStart(3, '0').includes(needle)
      );
    });
  }, [data, q, filter]);

  const captureRate = data.total > 0 ? Math.round((data.captured_count / (data.non_legendary_total || 146)) * 100) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-100 via-amber-50 to-sky-100 dark:from-slate-900 dark:to-slate-800" data-testid="student-pokedex">
      {/* Header */}
      <nav className="bg-rose-500/90 dark:bg-rose-700/90 backdrop-blur border-b-[3px] border-slate-900 sticky top-0 z-30">
        <div className="container mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/student/dashboard')} className="text-white hover:bg-white/20" data-testid="pokedex-back-btn">
            <ArrowLeft className="w-5 h-5"/>
          </Button>
          <div className="flex-1">
            <h1 className="font-outfit font-black text-xl text-white">Pokédex de Kanto</h1>
            <p className="text-[11px] text-rose-50">Companheiros encontrados ao concluir Jornadas</p>
          </div>
          <div className="rounded-full bg-white/20 px-3 py-1 text-white font-outfit font-black text-xs sm:text-sm" data-testid="pokedex-counter">
            {data.captured_count} / {data.non_legendary_total || 146}
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-5 space-y-4">
        {/* Progress bar */}
        <div className="bg-white dark:bg-slate-800 border-[3px] border-slate-900 rounded-2xl shadow-[3px_3px_0_rgba(15,23,42,0.85)] p-3">
          <div className="flex justify-between text-xs font-outfit font-black uppercase tracking-widest text-slate-500 mb-1">
            <span>Progresso de captura</span>
            <span>{captureRate}%</span>
          </div>
          <div className="h-3 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-rose-500 via-amber-400 to-emerald-400 transition-all" style={{ width: `${captureRate}%` }}/>
          </div>
        </div>

        {/* Search + filters */}
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nome ou número (ex: Pikachu, 025)"
              className="pl-10 border-2 border-slate-900"
              data-testid="pokedex-search"
            />
          </div>
          <div className="flex gap-1">
            {[
              { k: 'all', l: 'Todos' },
              { k: 'captured', l: 'Capturados' },
              { k: 'locked', l: 'Não vistos' },
            ].map((f) => (
              <button
                key={f.k}
                type="button"
                onClick={() => setFilter(f.k)}
                className={`px-3 py-1.5 rounded-full text-[11px] font-outfit font-black uppercase tracking-widest border-2 border-slate-900 transition-all ${filter===f.k ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-100'}`}
                data-testid={`pokedex-filter-${f.k}`}
              >
                {f.l}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="text-center py-16"><Loader2 className="w-10 h-10 animate-spin mx-auto text-rose-500"/></div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-7 lg:grid-cols-10 gap-2 sm:gap-3" data-testid="pokedex-grid">
            {filtered.map((e) => (
              <button
                key={e.pokemon_id}
                type="button"
                onClick={() => setSelected(e)}
                data-testid={`pokedex-entry-${e.pokemon_id}`}
                className={`group relative rounded-xl border-2 border-slate-900 p-2 flex flex-col items-center transition-all ${
                  e.captured
                    ? 'bg-white dark:bg-slate-800 shadow-[2px_2px_0_rgba(15,23,42,0.85)] hover:-translate-y-0.5'
                    : 'bg-slate-200 dark:bg-slate-700 hover:bg-slate-300'
                } ${e.is_legendary ? 'ring-2 ring-amber-400' : ''}`}
              >
                <span className="text-[9px] font-mono text-slate-400 self-end">#{String(e.pokemon_id).padStart(3,'0')}</span>
                <img
                  src={e.sprite_url}
                  alt={e.captured ? e.name : 'silhueta'}
                  loading="lazy"
                  decoding="async"
                  className="w-12 h-12 sm:w-14 sm:h-14"
                  style={{
                    imageRendering: 'pixelated',
                    filter: e.captured ? 'none' : 'brightness(0) saturate(100%)',
                    opacity: e.captured ? 1 : 0.45,
                  }}
                />
                <p className={`text-[10px] sm:text-xs font-outfit font-black mt-0.5 text-center ${e.captured ? 'text-slate-800 dark:text-slate-100' : 'text-slate-500'}`}>
                  {e.captured ? e.name : '???'}
                </p>
                {e.is_legendary && (
                  <span className="absolute top-1 left-1 text-[8px] font-outfit font-black bg-amber-400 text-slate-900 px-1 rounded">LEND</span>
                )}
              </button>
            ))}
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <p className="text-center text-slate-500 italic py-8">Nenhum Pokémon corresponde aos filtros.</p>
        )}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => { if (!v) setSelected(null); }}>
        <DialogContent className="max-w-md" data-testid="pokedex-detail">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl flex items-center gap-2">
              #{selected ? String(selected.pokemon_id).padStart(3,'0') : '???'} · {selected?.captured ? selected.name : 'Desconhecido'}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="text-center py-2">
              <img
                src={selected.sprite_url}
                alt={selected.captured ? selected.name : 'silhueta'}
                className="w-40 h-40 mx-auto"
                style={{
                  imageRendering: 'pixelated',
                  filter: selected.captured ? 'none' : 'brightness(0) saturate(100%)',
                  opacity: selected.captured ? 1 : 0.4,
                }}
              />
              {selected.captured ? (
                <>
                  <p className="font-outfit font-black text-2xl mt-2">{selected.name}</p>
                  <p className="text-xs text-slate-500 italic mt-1">
                    Capturado em {selected.captured_at ? new Date(selected.captured_at).toLocaleDateString('pt-BR') : '—'}
                  </p>
                  {selected.from_journey && (
                    <p className="text-xs text-slate-700 mt-1 flex items-center justify-center gap-1">
                      <Sparkles className="w-3 h-3 text-amber-500"/> Jornada: <strong>{selected.from_journey}</strong>
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p className="font-outfit font-black text-lg text-slate-500 mt-2">???</p>
                  <p className="text-xs text-slate-500 italic mt-1">
                    {selected.is_legendary
                      ? 'Lendário — não pode ser capturado em jornadas.'
                      : 'Conclua mais jornadas com ≥ 50% de acerto para revelar este Pokémon.'}
                  </p>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
